// The ocean mask: the shape painted over the interpolated field so the field only
// shows on land. Pure geometry, no DOM and no MapLibre — the map imports it, and
// src/lib/ocean-mask.test.js checks its invariants against the real basemap asset.

// MapLibre will not tessellate a polygon with more than 500 rings. Over that it
// keeps the 500 largest by area and silently drops the rest (`classifyRings(geom,
// EARCUT_MAX_RINGS)` in its fill bucket). For a mask made of holes that is not a
// detail: a dropped hole stops cutting, so the sea paints straight over whatever
// land it was holding open. It bites per *tile*, on geometry already clipped to
// that tile, so which landmasses survive depends on how much of each one happens to
// fall inside the tile — which is how Colombia, Cuba and Central America ended up
// under water at zoom 4 while the same mask was fine at zoom 5.
//
// So the choice is made HERE, by area, over the whole world, filling the cap to
// the ring below it: big land is never at the mercy of a per-tile area ranking.
// The islands that lose their hole would be painted over, so they are handed back
// as `mask-islands` and drawn on top of the mask instead. They forfeit the field
// wash, which at this land size is under one cell of a 0.5-degree grid.
const MAX_POLYGON_RINGS = 500;

// A [lon, lat] pair.
export type Position = readonly [number, number];
// A closed linear ring: the last position repeats the first.
export type Ring = Position[];
// One polygon's rings: exterior first, holes after.
export type PolygonRings = Ring[];
// [minX, minY, maxX, maxY].
export type BBox = [number, number, number, number];

// The subset of GeoJSON this reads: a land FeatureCollection of (Multi)Polygons.
export interface LandFeature {
  geometry?:
    | { type: 'Polygon'; coordinates: PolygonRings }
    | { type: 'MultiPolygon'; coordinates: PolygonRings[] }
    | null;
}
// The Natural Earth land layer, as handed in by the map or the test.
export interface LandCollection {
  features?: LandFeature[] | null;
}

// The sea shape: one MultiPolygon, holes for the landmasses it could keep.
export interface MaskFeature {
  type: 'Feature';
  geometry: { type: 'MultiPolygon'; coordinates: PolygonRings[] };
  properties: Record<string, unknown>;
}

// The islands the mask had to drop, drawn on top of it instead.
export interface IslandCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: { type: 'Polygon'; coordinates: PolygonRings };
    properties: Record<string, unknown>;
  }>;
}

// What buildOceanMask returns: the two layers, drawn in this order.
export interface OceanMask {
  mask: MaskFeature;
  islands: IslandCollection;
}

// A lake ring with the bounds used to reject non-candidates, and the islands
// found inside it.
interface Lake {
  ring: Ring;
  bbox: BBox;
  islands: Ring[];
}

// Clip the interpolated field to land. MapLibre can't clip one fill by another
// layer, so we paint an opaque ocean shape *on top of* the field. The field only
// shows through where that shape isn't; everywhere else is uniform water.
//
// A MultiPolygon of two kinds of piece:
//
//   1. The world, with a hole punched for every landmass. This is the sea.
//   2. One polygon per lake, each with a hole for any island in it. Lakes arrive as
//      interior rings of a land polygon, so they are already cut out of the land
//      fill; without painting them back the field would show through and a lake
//      would read as ground.
//
// Winding per polygon: exterior CCW, holes CW.
//
// An island in a lake must be a hole of its lake, never of the world: a hole inside
// a hole is not a shape a tessellator can make sense of, and the result is long
// black slivers thrown across the map. (ocean-mask.test.ts asserts both rules
// against the real asset, the Caspian and its islands included.)
//
// Returns the mask and, separately, the islands the mask could not keep — see
// MAX_POLYGON_RINGS.
function buildOceanMask(fc: LandCollection): OceanMask {
  const world: Ring = [
    [-180, -90],
    [180, -90],
    [180, 90],
    [-180, 90],
    [-180, -90],
  ]; // CCW exterior
  const polys: PolygonRings[] = [];
  for (const f of fc.features || []) {
    const g = f.geometry;
    if (!g) continue;
    if (g.type === 'Polygon') polys.push(g.coordinates);
    else if (g.type === 'MultiPolygon') polys.push(...g.coordinates);
  }
  // Every lake, with its bounds, so the containment test below rejects the rest of
  // the world in one comparison and only looks at genuine candidates.
  const lakes: Lake[] = [];
  for (const poly of polys) {
    for (let i = 1; i < poly.length; i++) {
      lakes.push({ ring: poly[i], bbox: bboxOf(poly[i]), islands: [] });
    }
  }

  const landRings: Ring[] = [];
  for (const poly of polys) {
    const outer = poly[0];
    if (!outer || outer.length < 4) continue;
    const lake = lakes.find((l) => bboxWithin(bboxOf(outer), l.bbox) && pointInRing(outer[0], l.ring));
    if (lake) lake.islands.push(windCW(outer));
    else landRings.push(outer);
  }

  // Keep the biggest landmasses, and only as many as a polygon is allowed. Which
  // ones we drop is our choice to make, not MapLibre's — see MAX_POLYGON_RINGS.
  landRings.sort((a, b) => Math.abs(signedArea(b)) - Math.abs(signedArea(a)));
  const kept = landRings.slice(0, MAX_POLYGON_RINGS - 1);
  const dropped = landRings.slice(MAX_POLYGON_RINGS - 1);

  return {
    mask: {
      type: 'Feature',
      geometry: {
        type: 'MultiPolygon',
        coordinates: [[world, ...kept.map(windCW)], ...lakes.map((l) => [windCCW(l.ring), ...l.islands])],
      },
      properties: {},
    },
    islands: {
      type: 'FeatureCollection',
      features: dropped.map((r) => ({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [windCCW(r)] },
        properties: {},
      })),
    },
  };
}

function bboxOf(ring: Ring): BBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return [minX, minY, maxX, maxY];
}

const bboxWithin = (a: BBox, b: BBox) => a[0] >= b[0] && a[1] >= b[1] && a[2] <= b[2] && a[3] <= b[3];

// Ray casting. The ring is closed (last point repeats the first), so the last
// vertex is skipped to avoid counting the closing edge twice.
function pointInRing([x, y]: Position, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 2; i < ring.length - 1; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

// Signed area (shoelace); >0 is CCW.
function signedArea(ring: Ring): number {
  let area = 0;
  for (let i = 0, n = ring.length - 1; i < n; i++) {
    area += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return area;
}

const windCW = (ring: Ring): Ring => (signedArea(ring) > 0 ? ring.slice().reverse() : ring); // holes
const windCCW = (ring: Ring): Ring => (signedArea(ring) > 0 ? ring : ring.slice().reverse()); // exteriors

export { buildOceanMask, MAX_POLYGON_RINGS, signedArea, pointInRing };
